// WALLET-P1: the Billing screens' query keys and reads. One wallet read per company, the
// History cursor-paginated like Setup › Costs, the spend per agent / task / day per month.
import type { OperationOutput } from "@companyos/sdk";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type WalletView = OperationOutput<"wallet.get">;
export type WalletLine = OperationOutput<"wallet.entries">["data"][number];
export type WalletSpend = OperationOutput<"wallet.spend">;
export type WalletMargin = OperationOutput<"operator.walletMargin.get">;
export type CatalogTariffRow = OperationOutput<"operator.tariffs.list">["data"][number];
export type BonusTier = OperationOutput<"operator.topupBonusTiers.list">["data"][number];
export type OperatorWalletView = OperationOutput<"operator.wallet.get">;

export const HISTORY_PAGE = 25;

/**
 * Usage debits send no change ping, so the open Billing page re-reads its wallet queries on this
 * interval (only while its tab is visible: react-query skips background refetches).
 */
export const WALLET_POLL_MS = 30_000;

export type SpendGroup = "agent" | "task" | "day" | "category";

export const billingKeys = {
  all: (co: string) => ["wallet", co] as const,
  wallet: (co: string) => ["wallet", co, "view"] as const,
  entries: (co: string) => ["wallet", co, "entries"] as const,
  run: (co: string, run: string) => ["wallet", co, "run", run] as const,
  dayCalls: (co: string, day: string) => ["wallet", co, "day-calls", day] as const,
  spend: (co: string, period: string, group: SpendGroup) =>
    ["wallet", co, "spend", period, group] as const,
  /** Operator › Billing. */
  operator: ["operator-billing"] as const,
  tariffs: () => ["operator-billing", "tariffs"] as const,
  tiers: () => ["operator-billing", "tiers"] as const,
  margin: (month: string) => ["operator-billing", "margin", month] as const,
  operatorWallet: (companyId: string) => ["operator-billing", "wallet", companyId] as const,
};

export const walletQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: billingKeys.wallet(co),
    queryFn: () => api.client.wallet.get({ params: { co } }),
  });

export const spendQuery = (api: Api, co: string, period: string, group: SpendGroup) =>
  queryOptions({
    queryKey: billingKeys.spend(co, period, group),
    queryFn: () => api.client.wallet.spend({ params: { co }, query: { period, group } }),
  });

/** A run's calls, newest page first; the drawer follows `next_cursor` with "Load more". */
export const runCallsQuery = (api: Api, co: string, run: string) =>
  infiniteQueryOptions({
    queryKey: billingKeys.run(co, run),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.wallet.entries({
        params: { co },
        query: { run, limit: 100, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

/** One UTC day's usage outside any run, one line per call (`?day=YYYY-MM-DD`). */
export const dayCallsQuery = (api: Api, co: string, day: string) =>
  infiniteQueryOptions({
    queryKey: billingKeys.dayCalls(co, day),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.wallet.entries({
        params: { co },
        query: { day, limit: 100, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

/** One Spend row's entries (usage and its corrections), one line per call, for the month window. */
export const spendEntriesQuery = (
  api: Api,
  co: string,
  group: SpendGroup,
  period: string,
  key: string,
) => {
  const { from, to } = monthWindow(period);
  return infiniteQueryOptions({
    queryKey: [...billingKeys.entries(co), "spend", period, group, key] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.wallet.entries({
        params: { co },
        query: {
          spend_group: group,
          spend_key: key,
          from,
          to,
          limit: 100,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
};

/** The month `YYYY-MM` as an inclusive-from / exclusive-to UTC window. */
export function monthWindow(period: string): { from: string; to: string } {
  const [y, m] = period.split("-").map(Number) as [number, number];
  return {
    from: new Date(Date.UTC(y, m - 1, 1)).toISOString(),
    to: new Date(Date.UTC(y, m, 1)).toISOString(),
  };
}

/** The wallet with the top-up named by `?topup=` (its `checkout` says paid or still open). */
export const checkoutQuery = (api: Api, co: string, checkout: string) =>
  queryOptions({
    queryKey: [...billingKeys.all(co), "checkout", checkout] as const,
    queryFn: () => api.client.wallet.get({ params: { co }, query: { checkout } }),
  });

export const walletMarginQuery = (api: Api, month: string) =>
  queryOptions({
    queryKey: billingKeys.margin(month),
    queryFn: () => api.client.operator.walletMargin.get({ query: { month } }),
  });

/** `YYYY-MM` of a date, UTC (the wallet's months are UTC). */
export function utcMonth(d: Date): string {
  return d.toISOString().slice(0, 7);
}

/** The last `count` UTC months, newest first. */
export function recentUtcMonths(today: Date, count = 12): string[] {
  return Array.from({ length: count }, (_, i) =>
    utcMonth(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - i, 1))),
  );
}

/** "October 2026". */
export function monthName(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}
